import request from '../utils/request';

export interface FundsAccount {
  id: number;
  customer_id: number;
  currency: string;
  available_balance: string;
  frozen_balance: string;
  status: string;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface FundsLedgerEntry {
  id: number;
  entry_no: string;
  account: number;
  customer_id: number;
  currency: string;
  biz_type: string;
  direction: 'credit' | 'debit';
  amount: string;
  delta_available: string;
  delta_frozen: string;
  balance_after_available: string;
  balance_after_frozen: string;
  status: string;
  source_type?: string;
  source_id?: number;
  source_code?: string;
  // 人类可读的关联业务标识（比如运单跟踪号）——跟 remark 分开：remark 是这次操作的
  // 具体原因（自由文本），source_label 是固定格式的业务标识，没有具体业务对象的记录
  // （比如员工手动冻结/解冻）留空。
  source_label?: string;
  remark?: string;
  created_at: string;
}

// 一笔具体的冻结——冻结明细列表、以及解冻时"选择要解冻哪几条记录"的选择器，用的都是
// 这个类型。status='frozen' 且 auto_release_at 有值，表示会被每日定时任务自动解冻
// （目前只有运费保证金是这样）；auto_release_at 为空表示没有固定期限，只能人工解冻。
export interface FundsFreezeRecord {
  id: number;
  account: number;
  customer_id: number;
  currency: string;
  amount: string;
  status: 'frozen' | 'released';
  source_type: string;
  source_id?: number;
  source_code?: string;
  // 人类可读的关联业务标识——运费保证金存运单跟踪号，人工冻结不填。跟 source_id（内部
  // 数据库自增 ID）不同，这个是给界面直接展示用的。
  source_label?: string;
  auto_release_at?: string | null;
  released_at?: string | null;
  freeze_entry: number;
  release_entry?: number | null;
  remark?: string;
  created_at: string;
}

export interface FundsFreezeResult {
  account: FundsAccount;
  ledger: FundsLedgerEntry;
  freeze_record: FundsFreezeRecord;
}

export interface FundsUnfreezeResult {
  account: FundsAccount;
  released: FundsFreezeRecord[];
  failed: { record_id: number; error: string }[];
}

export interface RechargeOrder {
  id: number;
  order_no: string;
  customer_id: number;
  account?: number;
  currency: string;
  requested_amount: string;
  settled_amount: string;
  channel: 'online' | 'offline';
  provider?: string;
  provider_trade_no?: string;
  voucher_image_url?: string;
  status: string;
  reviewer_id?: number;
  reviewed_at?: string | null;
  paid_at?: string | null;
  reject_reason?: string | null;
  remark?: string;
  created_at: string;
  updated_at: string;
}

export interface InvoicePayableLine {
  invoice_line_id: number;
  billing_record_id?: number;
  operation_type_id?: number;
  description?: string;
  line_amount: string;
  line_applied: string;
  line_remaining: string;
}

export interface InvoicePayableSummary {
  invoice_id: number;
  currency: string;
  payment_status: string;
  invoice_adjusted_total: string;
  invoice_paid_amount: string;
  invoice_outstanding_amount: string;
  selectable_remaining_total: string;
  adjustment_unallocated_remaining?: string;
  max_payable_now: string;
  lines: InvoicePayableLine[];
}

export interface PaymentOrder {
  id: number;
  order_no: string;
  invoice: number;
  customer_id: number;
  account?: number;
  currency: string;
  amount_requested: string;
  amount_executed: string;
  status: string;
}

export interface CurrencyExchangeOrder {
  id: number;
  order_no: string;
  customer_id: number;
  from_account: number;
  to_account: number;
  from_currency: string;
  to_currency: string;
  from_amount: string;
  to_amount_estimated: string;
  to_amount_settled: string;
  rate_snapshot?: number;
  status: string;
}

export interface FxRateSnapshot {
  id: number;
  pair_code: string;
  from_currency: string;
  to_currency: string;
  rate: string;
  source?: string;
  valid_from?: string;
  valid_to?: string;
  captured_at: string;
}

export interface ApiListResp<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

export interface PagedResult<T> {
  count: number;
  results: T[];
}

function normalizeList<T>(payload: any): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (Array.isArray(payload?.results)) return payload.results as T[];
  return [];
}

export async function fetchFundsAccounts(params?: { customer_id?: number; currency?: string }) {
  const resp = await request.get<ApiListResp<FundsAccount> | FundsAccount[]>(
    '/api/v1/billing/funds/accounts/',
    { params }
  );
  return normalizeList<FundsAccount>(resp.data);
}

export async function fetchFundsAccountsPaged(params?: {
  customer_id?: number;
  currency?: string;
  status?: string;
  page?: number;
  page_size?: number;
}): Promise<PagedResult<FundsAccount>> {
  const resp = await request.get<ApiListResp<FundsAccount> | FundsAccount[]>(
    '/api/v1/billing/funds/accounts/',
    { params }
  );
  const results = normalizeList<FundsAccount>(resp.data);
  const count = Number((resp.data as ApiListResp<FundsAccount>)?.count ?? results.length ?? 0);
  return { count, results };
}

export async function fetchFundsLedgerEntries(params?: {
  customer_id?: number;
  currency?: string;
  page_size?: number;
}) {
  const resp = await request.get<ApiListResp<FundsLedgerEntry> | FundsLedgerEntry[]>(
    '/api/v1/billing/funds/ledger-entries/',
    { params }
  );
  return normalizeList<FundsLedgerEntry>(resp.data);
}

export async function fetchFundsLedgerEntriesPaged(params?: {
  customer_id?: number;
  currency?: string;
  page?: number;
  page_size?: number;
}): Promise<PagedResult<FundsLedgerEntry>> {
  const resp = await request.get<ApiListResp<FundsLedgerEntry> | FundsLedgerEntry[]>(
    '/api/v1/billing/funds/ledger-entries/',
    { params }
  );
  const results = normalizeList<FundsLedgerEntry>(resp.data);
  const count = Number((resp.data as ApiListResp<FundsLedgerEntry>)?.count ?? results.length ?? 0);
  return { count, results };
}

export async function createManualFundsLedgerEntry(payload: {
  customer_id: number;
  account_id?: number;
  currency?: string;
  direction: 'credit' | 'debit';
  amount: number;
  remark: string;
  reason_type?: string;
}) {
  const resp = await request.post<FundsLedgerEntry>(
    '/api/v1/billing/funds/ledger-entries/manual-adjust/',
    payload
  );
  return resp.data;
}

// 冻结：给金额（或 full_amount 冻结全部可用余额）。
export async function executeFundsFreeze(
  accountId: number,
  payload: { amount?: number; full_amount?: boolean; remark: string }
) {
  const resp = await request.post<FundsFreezeResult>(
    `/api/v1/billing/funds/accounts/${accountId}/freeze-control/`,
    { action: 'freeze', ...payload }
  );
  return resp.data;
}

// 解冻：必须指定要解冻哪几条具体的冻结记录（record_ids），不再是随便填一个金额——
// 每条记录只能整条一次性解冻，不支持部分解冻。
export async function executeFundsUnfreeze(
  accountId: number,
  payload: { record_ids: number[]; remark: string }
) {
  const resp = await request.post<FundsUnfreezeResult>(
    `/api/v1/billing/funds/accounts/${accountId}/freeze-control/`,
    { action: 'unfreeze', ...payload }
  );
  return resp.data;
}

// 冻结明细：查某个账户当前（或历史）所有的冻结记录，用于"查看冻结明细"展示，也用于
// 解冻弹窗里"选择要解冻哪几条记录"的选择器（传 status: 'frozen' 过滤出还没解冻的）。
export async function fetchFundsFreezeRecords(params: {
  account?: number;
  customer_id?: number;
  currency?: string;
  status?: 'frozen' | 'released';
  page?: number;
  page_size?: number;
}): Promise<PagedResult<FundsFreezeRecord>> {
  const resp = await request.get<ApiListResp<FundsFreezeRecord> | FundsFreezeRecord[]>(
    '/api/v1/billing/funds/freeze-records/',
    { params }
  );
  const results = normalizeList<FundsFreezeRecord>(resp.data);
  const count = Number((resp.data as ApiListResp<FundsFreezeRecord>)?.count ?? results.length ?? 0);
  return { count, results };
}

export interface ShipmentAdjustmentResult {
  account: FundsAccount;
  ledger: FundsLedgerEntry;
}

// 物流运单运费调整（人工录入）：delta_amount 带符号，正数=向客户补缴，负数=退款给客户；
// 客户/币种后端会直接从 Shipment 自己的字段取，不需要在这里传。
export async function adjustShipmentFee(payload: {
  shipment_id: number;
  delta_amount: number;
  remark: string;
  idempotency_key?: string;
}) {
  const resp = await request.post<ShipmentAdjustmentResult>(
    '/api/v1/billing/funds/accounts/shipment-adjustment/',
    payload
  );
  return resp.data;
}

export async function createRechargeOrder(payload: {
  customer_id: number;
  currency: string;
  requested_amount: number;
  channel: 'online' | 'offline';
  provider?: string;
  provider_trade_no?: string;
  voucher_image_url?: string;
  voucher_files?: File[];
  status?: string;
  remark?: string;
}) {
  let resp;
  if (Array.isArray(payload.voucher_files) && payload.voucher_files.length > 0) {
    const fd = new FormData();
    fd.append('customer_id', String(payload.customer_id));
    fd.append('currency', String(payload.currency || '').toUpperCase());
    fd.append('requested_amount', String(payload.requested_amount));
    fd.append('channel', payload.channel);
    if (payload.provider) fd.append('provider', payload.provider);
    if (payload.provider_trade_no) fd.append('provider_trade_no', payload.provider_trade_no);
    if (payload.voucher_image_url) fd.append('voucher_image_url', payload.voucher_image_url);
    if (payload.status) fd.append('status', payload.status);
    if (payload.remark) fd.append('remark', payload.remark);
    payload.voucher_files.slice(0, 3).forEach((f) => {
      if (f instanceof File) fd.append('voucher_files', f);
    });
    resp = await request.post<RechargeOrder>('/api/v1/billing/funds/recharge-orders/', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } else {
    resp = await request.post<RechargeOrder>('/api/v1/billing/funds/recharge-orders/', payload);
  }
  return resp.data;
}

export async function approveRechargeOrder(id: number, settled_amount?: number) {
  const resp = await request.post<RechargeOrder>(
    `/api/v1/billing/funds/recharge-orders/${id}/approve/`,
    settled_amount ? { settled_amount } : {}
  );
  return resp.data;
}

export async function rejectRechargeOrder(id: number, reject_reason?: string) {
  const resp = await request.post<RechargeOrder>(
    `/api/v1/billing/funds/recharge-orders/${id}/reject/`,
    reject_reason ? { reject_reason } : {}
  );
  return resp.data;
}

export async function fetchRechargeOrders(params?: {
  customer_id?: number;
  currency?: string;
  status?: string;
  channel?: 'online' | 'offline';
  page_size?: number;
}) {
  const resp = await request.get<ApiListResp<RechargeOrder> | RechargeOrder[]>(
    '/api/v1/billing/funds/recharge-orders/',
    { params }
  );
  return normalizeList<RechargeOrder>(resp.data);
}

export async function fetchRechargeOrdersPaged(params?: {
  customer_id?: number;
  currency?: string;
  status?: string;
  channel?: 'online' | 'offline';
  page?: number;
  page_size?: number;
}): Promise<PagedResult<RechargeOrder>> {
  const resp = await request.get<ApiListResp<RechargeOrder> | RechargeOrder[]>(
    '/api/v1/billing/funds/recharge-orders/',
    { params }
  );
  const results = normalizeList<RechargeOrder>(resp.data);
  const count = Number((resp.data as ApiListResp<RechargeOrder>)?.count ?? results.length ?? 0);
  return { count, results };
}

export async function fetchInvoicePayableSummary(invoiceId: number) {
  const resp = await request.get<InvoicePayableSummary>(`/api/v1/billing/invoices/${invoiceId}/payable_summary/`);
  return resp.data;
}

export async function createPaymentOrder(payload: {
  invoice: number;
  customer_id: number;
  currency: string;
  amount_requested: number;
  pay_mode?: 'balance';
  remark?: string;
}) {
  const resp = await request.post<PaymentOrder>('/api/v1/billing/funds/payment-orders/', {
    pay_mode: 'balance',
    ...payload,
  });
  return resp.data;
}

export async function executeSelectivePaymentOrder(
  paymentOrderId: number,
  payload: { amount?: number }
) {
  const resp = await request.post<PaymentOrder>(
    `/api/v1/billing/funds/payment-orders/${paymentOrderId}/execute_selective/`,
    payload
  );
  return resp.data;
}

export async function createExchangeOrder(payload: {
  customer_id: number;
  from_account: number;
  to_account: number;
  from_currency: string;
  to_currency: string;
  from_amount: number;
  to_amount_estimated: number;
  rate_snapshot?: number;
  remark?: string;
}) {
  const resp = await request.post<CurrencyExchangeOrder>('/api/v1/billing/funds/exchange-orders/', payload);
  return resp.data;
}

export async function executeExchangeOrder(exchangeOrderId: number) {
  const resp = await request.post<CurrencyExchangeOrder>(
    `/api/v1/billing/funds/exchange-orders/${exchangeOrderId}/execute/`,
    {}
  );
  return resp.data;
}

export async function fetchFxRateSnapshots(params?: {
  from_currency?: string;
  to_currency?: string;
  page_size?: number;
}) {
  const resp = await request.get<ApiListResp<FxRateSnapshot> | FxRateSnapshot[]>(
    '/api/v1/billing/funds/fx-rates/',
    { params }
  );
  return normalizeList<FxRateSnapshot>(resp.data);
}
